import { ofetch } from 'ofetch';
import qs from 'qs';
import { getConstants } from '~/utils/constants';

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const router = useRouter();
  const { PUBLIC_PATHS } = getConstants();
  // Avoid calling `useI18n()` here (must be used in setup). Use `nuxtApp.$i18n` safely.
  const t = (key) => (nuxtApp.$i18n?.t ? nuxtApp.$i18n.t(key) : key);

  // Constants
  const BASE_URL = config.public.apiUrl;
  const TIMEOUT = 30000;
  const RETRY_DELAY = 1000;
  const MAX_RETRIES = 1; // Disabled retries to fix triple requests issue

  // State
  const activeRequests = new Map();
  let isRefreshingToken = false;
  let failedRequestsQueue = [];
  const skipNotificationUrls = new Set();

  const logout = () => {
    const authCookie = useCookie('Authorization');
    authCookie.value = null;
    const currentRoute = router.currentRoute.value.name;

    if (!PUBLIC_PATHS.includes(currentRoute)) {
      router.push('/login');
    }
  };

  const processQueue = (error, token) => {
    failedRequestsQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    failedRequestsQueue = [];
  };

  const handleRefreshToken = async () => {
    isRefreshingToken = true;

    try {
      const token = useCookie('Authorization').value;
      const response = await ofetch(`${BASE_URL}/auth/refresh-token`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const newToken = response?.data?.token;
      if (newToken) {
        const authCookie = useCookie('Authorization', {
          maxAge: 365 * 24 * 60 * 60,
        });
        authCookie.value = newToken;
        processQueue(null, newToken);
        return newToken;
      } else {
        throw new Error('No token in refresh response');
      }
    } catch (error) {
      processQueue(error, null);
      logout();
      throw error;
    } finally {
      isRefreshingToken = false;
    }
  };

  const apiFetch = ofetch.create({
    baseURL: BASE_URL,
    timeout: TIMEOUT,
    retry: MAX_RETRIES,
    retryDelay: RETRY_DELAY,

    onRequest({ request, options }) {
      const token = useCookie('Authorization').value;

      options.headers = {
        ...(options.headers || {}),
        Authorization: token ? `Bearer ${token}` : undefined,
        lang: nuxtApp.$i18n?.locale?.value || 'it',
      };

      // Only set Content-Type to application/json if body is not FormData
      if (!(options.body instanceof FormData)) {
        options.headers['Content-Type'] = 'application/json';
      }

      if (token) {
        options.headers['Authorization'] = `Bearer ${token}`;
      }

      // Track if this request should skip notification
      if (options.skipNotification) {
        skipNotificationUrls.add(request.toString());
      }

      // Cancellation logic
      // Unique key for cancellation: Method + URL
      const method = options.method || 'GET';
      const requestKey = `${method.toUpperCase()}_${request}`;
      const whiteList = ['auth/refresh-token'];

      // If allowDuplicate is NOT set, cancel previous
      const isWhitelisted = whiteList.some((w) =>
        request.toString().includes(w)
      );

      if (!isWhitelisted && !options.allowDuplicate) {
        if (activeRequests.has(requestKey)) {
          activeRequests.get(requestKey).abort();
          activeRequests.delete(requestKey);
        }
        const controller = new AbortController();
        options.signal = controller.signal;
        activeRequests.set(requestKey, controller);
      }
    },

    onResponse({ request, response, options }) {
      const method = options.method || 'GET';
      const requestKey = `${method.toUpperCase()}_${request}`;
      activeRequests.delete(requestKey);

      // Show success toast if skipNotification is not set and response has a message
      if (method !== 'GET' && !options.skipNotification && response._data?.msg) {
        const msg = response._data.msg;

        if (response.status === 200) {
          try {
            const { showSuccess } = useNotification();
            showSuccess(msg);
          } catch (e) {
            const title = nuxtApp.$i18n?.t
              ? nuxtApp.$i18n.t('generic.success')
              : typeof t === 'function'
                ? t('generic.success')
                : undefined;
            nuxtApp.$toast?.({ message: msg, title, type: 'success' });
          }
        }
      }

      // Clean up skipNotification tracking
      skipNotificationUrls.delete(request.toString());
    },

    async onResponseError({ request, response, options }) {
      const method = options.method || 'GET';
      const requestKey = `${method.toUpperCase()}_${request}`;
      // Ensure cleanup happens even on error responses if onResponse wasn't triggered or didn't clean up
      if (activeRequests.has(requestKey)) {
        activeRequests.delete(requestKey);
      }

      const status = response?.status;

      // Show error toast if skipNotification is not set
      // Status 402 is for token refresh, so skip notification like 401
      if (!options.skipNotification && status !== 401 && status !== 402) {
        const errorMessage =
          response._data?.message ||
          response._data?.msg ||
          response.statusText ||
          t('generic.error');
        try {
          const { showError } = useNotification();
          showError(errorMessage);
        } catch (e) {
          const title = nuxtApp.$i18n?.t
            ? nuxtApp.$i18n.t('generic.error')
            : typeof t === 'function'
              ? t('generic.error')
              : undefined;
          nuxtApp.$toast?.({ message: errorMessage, title, type: 'danger' });
        }
      }

      // Clean up skipNotification tracking
      skipNotificationUrls.delete(request.toString());

      switch (status) {
        case 401:
          logout();
          break;

        case 402:
          if (!isRefreshingToken) {
            try {
              // Retry original request
              const newToken = await handleRefreshToken();
              options.headers['Authorization'] = `Bearer ${newToken}`;
              // Remove signal to avoid abort on retry
              delete options.signal;
              return apiFetch(request, options);
            } catch (e) {
              throw e;
            }
          } else {
            // Queue
            return new Promise((resolve, reject) => {
              failedRequestsQueue.push({ resolve, reject });
            }).then((token) => {
              options.headers['Authorization'] = `Bearer ${token}`;
              // Remove signal to avoid abort on retry
              delete options.signal;
              return apiFetch(request, options);
            });
          }

        case 505:
          if (import.meta.client) {
            window.location.reload();
          }
          break;
      }

      // Normalize and rethrow error so callers receive a plain Error
      try {
        const errMsg =
          response._data?.message ||
          response._data?.msg ||
          response.statusText ||
          'API Error';
        const normalizedError = new Error(errMsg);
        normalizedError.data = response._data;
        normalizedError.status = status;
        throw normalizedError;
      } catch (e) {
        // If something unexpected happens while normalizing, rethrow original
        throw e;
      }
    },

    onRequestError({ request, options }) {
      const method = options.method || 'GET';
      const requestKey = `${method.toUpperCase()}_${request}`;
      if (activeRequests.has(requestKey)) {
        activeRequests.delete(requestKey);
      }
    },
  });

  const wrappedApi = async (url, options = {}) => {
    let finalUrl = url;
    // Handle legacy /! prefix which means "allow duplicate"
    if (typeof finalUrl === 'string' && finalUrl.startsWith('!')) {
      finalUrl = finalUrl.replace('!', '');
      options.allowDuplicate = true;
    }

    if (
      options?.query &&
      (!options.method || options.method.toUpperCase() === 'GET')
    ) {
      const queryString = qs.stringify(options.query, {
        arrayFormat: 'brackets',
      });
      finalUrl = `${finalUrl}?${queryString}`;
      delete options.query;
    }

    try {
      return await apiFetch(finalUrl, options);
    } catch (error) {
      if (
        error.name === 'AbortError' ||
        error.message?.toLowerCase().includes('aborted')
      ) {
        // Return a pending promise to avoid triggering .then() or .catch() on cancelled requests
        return new Promise(() => { });
      }

      // Transform error to include msg and error properties for FormWrapper
      const transformedError = new Error(error.message);
      transformedError.msg =
        error.data?.message || error.data?.msg || error.message;
      // Support both 'errors' (plural) and 'error' (singular) formats from server
      transformedError.error = error.data?.errors || error.data?.error || {};
      transformedError.status = error.status;

      throw transformedError;
    }
  };

  // Verbs
  wrappedApi.get = (url, opts) => wrappedApi(url, { query: opts, method: 'GET' });
  wrappedApi.post = (url, body, opts) =>
    wrappedApi(url, { ...opts, method: 'POST', body });
  wrappedApi.put = (url, body, opts) =>
    wrappedApi(url, { ...opts, method: 'PUT', body });
  wrappedApi.patch = (url, body, opts) =>
    wrappedApi(url, { ...opts, method: 'PATCH', body });
  wrappedApi.delete = (url, opts) =>
    wrappedApi(url, { ...opts, method: 'DELETE' });
  wrappedApi.download = (url, params) => {
    return wrappedApi(`${url}/download`, {
      query: params,
      method: 'GET',
      headers: {
        'Accept': 'application/octet-stream, application/blob, audio/*',
      },
    });
  };
  return {
    provide: {
      api: wrappedApi,
    },
  };
});
